import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Tag,
  Text,
  VStack,
  HStack,
  Radio,
  RadioGroup,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';

import DateField from 'components/fields/DateField';
import SelectSearch from 'components/fields/SelectSearch';
import { formatDateToString } from 'utils/helpers';
import { getApiErrorMessage } from 'utils/apiFieldErrors';
import {
  LOB_PERSONAL_AUTO,
  LOB_PL_SPECIALTY,
  LOB_CL_PROPERTY,
} from 'variables/constants';
import {
  apiInitiateQuote,
  apiInitiateRFQQuote,
  apiGetQuoteInitiationStatus,
  apiGetAnswerCodes,
  apiGetProductTargets,
  ActurisContactDetails,
  SubmissionRoute,
} from 'api/quote-marketplace';
import { usePropertyTypeOptions } from '../hooks';
import { isEffectiveDateWarningCode } from '../constants';
import { EffectiveDateWarningBanner } from './EffectiveDateWarningBanner';
import type { StartQuoteSelection } from './StartQuoteCard';

const POLL_INTERVAL_MS = 1000;
const TERM_SELECTION_PROVINCES = [1, 5, 7]; // ON, NB, NS
const ROUTE_LINE_OF_BUSINESS: Record<SubmissionRoute, number> = {
  pl_specialty: LOB_PL_SPECIALTY,
  cl_property: LOB_CL_PROPERTY,
};

interface StartQuoteModalProps {
  selection: StartQuoteSelection | null;
  contact: ActurisContactDetails | null;
  onClose: () => void;
  onSuccess: (quoteId: number, lineOfBusiness: number) => void;
  onRfqCreated: (quoteId: number, rfqRef: string) => void;
}

const formatLabelList = (items: string[]): string =>
  items.length > 1
    ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
    : items.join('');

const getDefaultEffectiveDate = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return date;
};

const StartQuoteModal = ({
  selection,
  contact,
  onClose,
  onSuccess,
  onRfqCreated,
}: StartQuoteModalProps) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.300');

  const [effectiveDate, setEffectiveDate] = useState<Date | null>(
    getDefaultEffectiveDate(),
  );
  const [locationType, setLocationType] = useState<string>('');
  const [termMonths, setTermMonths] = useState<6 | 12>(12);
  const [routeProductTarget, setRouteProductTarget] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const quoteIdRef = useRef<number | null>(null);

  const route = selection?.route;
  const isRoute = !!route;
  const isProperty =
    selection !== null &&
    !isRoute &&
    selection.lineOfBusiness !== LOB_PERSONAL_AUTO;
  const isAuto =
    selection !== null && selection.lineOfBusiness === LOB_PERSONAL_AUTO;
  const effectiveDateStr = effectiveDate
    ? formatDateToString(effectiveDate)
    : null;
  const productTarget = isProperty ? 'habitational' : 'auto';

  const showTermSelector =
    isAuto &&
    contact?.province !== null &&
    TERM_SELECTION_PROVINCES.includes(contact?.province ?? 0);

  const { options: propertyTypes, isLoading: isLoadingTypes } =
    usePropertyTypeOptions(effectiveDateStr, isProperty);

  const { data: answerCodesData } = useQuery({
    queryKey: ['acturis', 'answer-codes', effectiveDateStr, productTarget],
    queryFn: () => apiGetAnswerCodes(effectiveDateStr, productTarget),
    enabled: !!effectiveDateStr && selection !== null && !isRoute,
    staleTime: 1000 * 60 * 5,
  });

  const { data: routeProductTargetsData, isLoading: isLoadingRouteTypes } =
    useQuery({
      queryKey: ['quote-marketplace', 'product-targets', contact?.office_ref],
      queryFn: () =>
        apiGetProductTargets(contact!.contact_ref, contact!.office_ref!),
      enabled: isRoute && !!contact?.office_ref,
      staleTime: 1000 * 60 * 60,
    });

  const routeTypeOptions = useMemo(
    () => routeProductTargetsData?.product_targets ?? [],
    [routeProductTargetsData],
  );

  const dateWarning = useMemo(() => {
    const warning = answerCodesData?.warning;
    if (!warning || !isEffectiveDateWarningCode(warning.code)) return null;
    return {
      code: warning.code,
      requestedDate: warning.requested_date,
      validFrom: warning.valid_from,
      validTo: warning.valid_to,
    };
  }, [answerCodesData]);

  const hasDateWarning = dateWarning !== null;

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    quoteIdRef.current = null;
    setIsPolling(false);
  }, []);

  const resetState = useCallback(() => {
    setEffectiveDate(getDefaultEffectiveDate());
    setLocationType('');
    setTermMonths(12);
    setRouteProductTarget('');
    setIsSubmitting(false);
    setError(null);
    stopPolling();
  }, [stopPolling]);

  useEffect(() => {
    if (selection) {
      resetState();
    }
  }, [selection, resetState]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    if (isLoadingTypes || !locationType) return;
    if (!propertyTypes.some((type) => type.code === locationType)) {
      setLocationType('');
    }
  }, [propertyTypes, isLoadingTypes, locationType]);

  const typeOptions = useMemo(
    () =>
      propertyTypes.map((type) => ({ value: type.code, label: type.label })),
    [propertyTypes],
  );

  const missingRefLabels = useMemo(() => {
    if (!contact) return [];
    const labels: string[] = [];
    if (!contact.office_ref) labels.push('office');
    if (!contact.executive_ref) labels.push('executive');
    if (!contact.handler_ref) labels.push('handler');
    return labels;
  }, [contact]);

  const hasRequiredRefs = contact !== null && missingRefLabels.length === 0;

  const isValid =
    effectiveDate !== null &&
    !hasDateWarning &&
    hasRequiredRefs &&
    (!isProperty || locationType !== '') &&
    (!isRoute || routeProductTarget !== '');

  const pollStatus = useCallback(async () => {
    const quoteId = quoteIdRef.current;
    if (!quoteId || !selection) return;

    try {
      const status = await apiGetQuoteInitiationStatus(quoteId);

      if (
        status.status === 'complete' &&
        status.quote_id &&
        selection.lineOfBusiness !== undefined
      ) {
        stopPolling();
        onSuccess(status.quote_id, selection.lineOfBusiness);
      } else if (status.status === 'error') {
        stopPolling();
        const errorMsg = status.error_message || 'Quote creation failed';
        setError(`Quote #${quoteId}: ${errorMsg}`);
      }
    } catch {
      stopPolling();
      setError(`Quote #${quoteId}: Failed to check quote creation status`);
    }
  }, [selection, stopPolling, onSuccess]);

  const startPolling = useCallback(
    (quoteId: number) => {
      quoteIdRef.current = quoteId;
      setIsPolling(true);

      pollStatus();

      pollIntervalRef.current = setInterval(() => {
        pollStatus();
      }, POLL_INTERVAL_MS);
    },
    [pollStatus],
  );

  const handleSubmit = async () => {
    if (!isValid || !effectiveDateStr || !selection || !contact) return;

    setIsSubmitting(true);
    setError(null);

    if (selection.route) {
      try {
        const response = await apiInitiateRFQQuote({
          contactRef: contact.contact_ref,
          lineOfBusiness: ROUTE_LINE_OF_BUSINESS[selection.route],
          productTarget: routeProductTarget,
          effectiveDate: effectiveDateStr,
          contactData: contact,
        });
        setIsSubmitting(false);
        onRfqCreated(response.quote_id, response.rfq_ref);
      } catch (err) {
        setIsSubmitting(false);
        setError(
          getApiErrorMessage(
            err as Record<string, string | string[]>,
            'Failed to create the RFQ',
          ),
        );
      }
      return;
    }

    try {
      const response = await apiInitiateQuote({
        contactRef: contact.contact_ref,
        lineOfBusiness: selection.lineOfBusiness,
        effectiveDate: effectiveDateStr,
        locationType: isProperty ? locationType : undefined,
        contactData: contact,
        termMonths: showTermSelector ? termMonths : undefined,
      });

      setIsSubmitting(false);
      startPolling(response.quote_id);
    } catch (err) {
      setIsSubmitting(false);
      const errorObj = err as Record<string, string | string[]>;
      setError(
        getApiErrorMessage(errorObj, 'Failed to initiate quote creation'),
      );
    }
  };

  const handleClose = () => {
    if (isPolling) return;
    stopPolling();
    onClose();
  };

  const lineLabel =
    selection?.label ?? (isProperty ? 'PL Property' : 'PL Auto');
  const isLoading = isSubmitting || isPolling;

  return (
    <Modal
      isOpen={selection !== null}
      onClose={handleClose}
      isCentered
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color={textColor}>Start New Quote</ModalHeader>
        {!isLoading && <ModalCloseButton />}
        <ModalBody>
          {isPolling ? (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color="brand.500" thickness="4px" />
              <Text color={textColor} fontWeight="500">
                Creating an RFQ...
              </Text>
              <Text fontSize="sm" color={labelColor}>
                Please wait while we set up your request for a quote
              </Text>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <VStack spacing={2} align="start">
                <Text fontSize="sm" fontWeight="600" color={labelColor}>
                  Line of Business
                </Text>
                <Tag colorScheme="brand" size="md">
                  {lineLabel}
                </Tag>
              </VStack>

              <DateField
                label="Effective Date"
                date={effectiveDate}
                required
                onChange={(date: Date | null) => setEffectiveDate(date)}
              />

              {dateWarning && (
                <EffectiveDateWarningBanner
                  requestedDate={dateWarning.requestedDate}
                  validFrom={dateWarning.validFrom}
                  validTo={dateWarning.validTo}
                  warningCode={dateWarning.code}
                  onNavigate={() => {}}
                  showNavigateButton={false}
                />
              )}

              {showTermSelector && (
                <FormControl>
                  <FormLabel>Term Length</FormLabel>
                  <RadioGroup
                    value={String(termMonths)}
                    onChange={(val) => setTermMonths(Number(val) as 6 | 12)}
                  >
                    <HStack spacing={6}>
                      <Radio value="6" colorScheme="brand">
                        6 Months
                      </Radio>
                      <Radio value="12" colorScheme="brand">
                        12 Months
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>
              )}

              {isProperty && (
                <FormControl isRequired>
                  <FormLabel>Type</FormLabel>
                  <SelectSearch
                    options={typeOptions}
                    value={locationType}
                    onChange={(value) => setLocationType(String(value ?? ''))}
                    placeholder="Select risk type..."
                    isLoading={isLoadingTypes}
                    isDisabled={isLoadingTypes || typeOptions.length === 0}
                  />
                </FormControl>
              )}

              {isRoute && (
                <FormControl isRequired>
                  <FormLabel>Type</FormLabel>
                  <SelectSearch
                    options={routeTypeOptions}
                    value={routeProductTarget}
                    onChange={(value) =>
                      setRouteProductTarget(String(value ?? ''))
                    }
                    placeholder="Select risk type..."
                    isLoading={isLoadingRouteTypes}
                    isDisabled={
                      isLoadingRouteTypes || routeTypeOptions.length === 0
                    }
                  />
                </FormControl>
              )}

              {isRoute &&
                !isLoadingRouteTypes &&
                routeTypeOptions.length === 0 && (
                  <Alert status="warning" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    Types could not be loaded. Please try again.
                  </Alert>
                )}

              {isProperty && !isLoadingTypes && typeOptions.length === 0 && (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Property types could not be loaded. Please try again.
                </Alert>
              )}

              {!hasRequiredRefs && (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  <AlertDescription>
                    {contact === null
                      ? 'Contact details could not be loaded, so a quote cannot be started. Please refresh the page and try again.'
                      : `This contact is missing an assigned ${formatLabelList(
                          missingRefLabels,
                        )} in Acturis. ${
                          missingRefLabels.length > 1 ? 'These are' : 'This is'
                        } required to start a quote.`}
                  </AlertDescription>
                </Alert>
              )}
            </VStack>
          )}
        </ModalBody>
        {!isPolling && (
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!isValid}
            >
              Start Quote
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default StartQuoteModal;
